import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'
import StaffArchive from './StaffArchive'
import Header from '../../components/Header'
import { useNavigate } from 'react-router-dom'

function Pagination({ page, totalItems, pageSize, onPageChange }) {
  const totalPages = Math.ceil(totalItems / pageSize)
  if (totalPages <= 1) return null

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
      <span className="text-sm text-gray-600">
        {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium"
          style={{ color: page === 1 ? '#d1d5db' : '#374151', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>
          ← Prev
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
          .reduce((acc, n, idx, arr) => {
            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
            acc.push(n)
            return acc
          }, [])
          .map((n, idx) =>
            n === "…" ? (
              <span key={`e-${idx}`} className="text-sm text-gray-400 px-1">…</span>
            ) : (
              <button key={n} onClick={() => onPageChange(n)}
                className="w-8 h-8 rounded-lg text-sm font-semibold"
                style={{
                  border: `1px solid ${n === page ? '#FF6F3C' : '#d1d5db'}`,
                  background: n === page ? '#FF6F3C' : '#fff',
                  color: n === page ? '#fff' : '#374151',
                  cursor: 'pointer',
                }}>
                {n}
              </button>
            )
          )
        }
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium"
          style={{ color: page === totalPages ? '#d1d5db' : '#374151', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>
          Next →
        </button>
      </div>
    </div>
  )
}

function StaffFormFields({ formData, setFormData, branches, mode }) {
  const isAdd = mode === 'add'

  return (
    <>
      {/* Personal Information — read-only, sourced from the branch's Branch Owner */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-600">
          <p>
            Name, email, and phone number for this account are pulled from the assigned
            branch's <strong>Branch Owner</strong> details. To update them, edit the branch
            under Branch Management.
          </p>
        </div>
      </div>

      {/* Account Type and Branch */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Type & Branch</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">          <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
          >
            <option value="">{isAdd ? 'Select account type...' : 'Select Role...'}</option>
            <option value="Barista">Barista</option>
            <option value="Admin">Admin</option>
          </select>
        </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned Branch <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.assignedBranch}
              onChange={(e) => setFormData({ ...formData, assignedBranch: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
            >
              <option value="">Select branch...</option>
              {branches.map((branch) => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isAdd && formData.role && formData.assignedBranch && (
          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-500 mb-1">Generated Login Email</p>
            <p className="text-sm font-mono font-medium text-gray-800">{formData.companyEmail}</p>
          </div>
        )}
      </div>

    </>
  )
}

// Default permissions based on Account Type — Admins get full access,
// Baristas get order/inventory access only.
function getPermissionsByRole(role) {
  if (role === 'Admin') {
    return {
      canViewAnalytics: true,
      canManageOrders: true,
      canAccessCustomerData: true,
      canModifyInventory: true
    }
  }
  return {
    canViewAnalytics: false,
    canManageOrders: true,
    canAccessCustomerData: false,
    canModifyInventory: true
  }
}

// Splits a Branch Owner's full name into first/last for the staff record
function splitOwnerName(fullName = '') {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first: '', last: '' }
  if (parts.length === 1) return { first: parts[0], last: parts[0] }
  return { first: parts[0], last: parts.slice(1).join(' ') }
}

export default function StaffManagement({ onGoToProfile }) {
  const [branches, setBranches] = useState([])

  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [filterRole, setFilterRole] = useState("All")
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingStaff, setEditingStaff] = useState(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingStaff, setViewingStaff] = useState(null)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [archivingStaff, setArchivingStaff] = useState(null)
  const [archiveReason, setArchiveReason] = useState("")
  const [showArchiveView, setShowArchiveView] = useState(false)
  const [originalFormData, setOriginalFormData] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const [stats, setStats] = useState({
    totalStaff: 0,
    activeBaristas: 0,
    admins: 0,
    branches: 0
  })

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyEmail: "",
    phoneNumber: "",
    role: "",
    assignedBranch: "",
    employmentType: "",
    permissions: {
      canViewAnalytics: false,
      canManageOrders: false,
      canAccessCustomerData: false,
      canModifyInventory: false
    }
  })

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
            .swal2-confirm {
                background-color: #FF6F3C !important;
                border: none !important;
            }
            .swal2-confirm:hover {
                background-color: #ff4c0b !important;
            }
            .swal2-cancel {
                background-color: #6b7280 !important;
                border: none !important;
            }
            .swal2-cancel:hover {
                background-color: #5a5a5a !important;
            }
        `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    fetchstaff()
    fetchBranches()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, filterRole])

  // Auto-generate the system login email — only while the Add modal is open,
  // so it doesn't overwrite an existing staff member's email during Edit.
  useEffect(() => {
    if (showAddModal && formData.role && formData.assignedBranch) {
      const branch = branches.find(b => b.branch_id === formData.assignedBranch)
      if (branch) {
        const branchSlug = branch.name
          .replace(/[^a-zA-Z\s]/g, '')
          .trim()
          .split(/\s+/)
          .filter(Boolean)
          .map(w => w[0])
          .join('')
          .toLowerCase()
        const roleSlug = formData.role.toLowerCase().slice(0, 3)
        setFormData(prev => ({ ...prev, companyEmail: `${branchSlug}-${roleSlug}@kaperk.com` }))
      }
    }
  }, [formData.role, formData.assignedBranch, showAddModal, branches])

  useEffect(() => {
    if (!showArchiveView) {
      fetchstaff()
    }
  }, [showArchiveView])

  const getFriendlyError = (err) => {
    const msg = err?.message?.toLowerCase() || ""
    if (err?.code === "23505" || msg.includes("duplicate key") || msg.includes("unique"))
      return "A staff member with this email already exists. Please use a different email."
    if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed to fetch"))
      return "Network error. Please check your internet connection and try again."
    if (msg.includes("jwt") || msg.includes("auth") || msg.includes("unauthorized"))
      return "Your session has expired. Please log in again."
    if (msg.includes("permission") || msg.includes("rls"))
      return "You don't have permission to perform this action."
    if (msg.includes("timeout"))
      return "The request timed out. Please try again."
    return "Something went wrong. Please try again or contact support if the issue persists."
  }

  const fetchBranches = async () => {
    try {
      const { data, error } = await supabase
        .from('branches')
        .select('branch_id, name, status, branch_owner, email, phone_number')
        .order('name', { ascending: true })

      if (error) throw error

      setBranches((data || []).filter(branch => branch.status !== 'inactive'))
    } catch (err) {
      console.error("Error fetching branches:", err)
    }
  }

  const fetchstaff = async () => {
    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('staff')
        .select(`
          *,
          branches!staff_branch_id_fkey (
            branch_id,
            name
          )
        `)
        .order('start_date', { ascending: false })

      if (error) throw error

      setStaff(data || [])
      calculateStats(data || [])
    } catch (err) {
      console.error("Error fetching staff: ", err)
      Swal.fire({
        icon: "error",
        title: "Failed to Load Staff",
        text: getFriendlyError(err),
        confirmButtonColor: "#FF6F3C"
      })
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = (staffData) => {
    const totalStaff = staffData.length
    const activeBaristas = staffData.filter(
      e => e.role === "Barista" && e.status === "Active"
    ).length
    const admins = staffData.filter(
      e => e.role === "Admin" && e.status === "Active"
    ).length
    const branches = new Set(staffData.map(e => e.branch_id).filter(Boolean)).size

    setStats({ totalStaff, activeBaristas, admins, branches })
  }

  const handleBackFromArchive = () => {
    setShowArchiveView(false)
  }

  if (showArchiveView) {
    return (
      <StaffArchive
        onBack={handleBackFromArchive}
        onStaffRestored={fetchstaff}
      />
    )
  }

  const validateForm = () => {
    if (formData.firstName.length > 30) {
      Swal.fire({
        icon: "warning",
        title: "Invalid First Name",
        text: "First name must not exceed 30 characters",
        confirmButtonColor: "#FF6F3C"
      })
      return false
    }

    const nameRegex = /^[a-zA-Z\s]+$/
    if (!nameRegex.test(formData.firstName)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid First Name",
        text: "First name must contain only letters",
        confirmButtonColor: "#FF6F3C"
      })
      return false
    }

    if (formData.lastName.length > 30) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Last Name",
        text: "Last name must not exceed 30 characters",
        confirmButtonColor: "#FF6F3C"
      })
      return false
    }

    if (!nameRegex.test(formData.lastName)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Last Name",
        text: "Last name must contain only letters",
        confirmButtonColor: "#FF6F3C"
      })
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Email",
        text: "Please enter a valid email address",
        confirmButtonColor: "#FF6F3C"
      })
      return false
    }

    const phoneRegex = /^(09|\+639)\d{9}$/
    const cleanPhone = formData.phoneNumber.replace(/[\s-]/g, '')
    if (!phoneRegex.test(cleanPhone)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Phone Number",
        text: "Please enter a valid Philippine phone number (e.g., 09123456789 or +639123456789)",
        confirmButtonColor: "#FF6F3C"
      })
      return false
    }

    if (formData.startDate) {
      const selectedDate = new Date(formData.startDate)
      const today = new Date()
      today.setHours(23, 59, 59, 999)

      if (selectedDate > today) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Start Date",
          text: "Start date cannot be in the future",
          confirmButtonColor: "#FF6F3C"
        })
        return false
      }
    }

    return true
  }

  const handleAddStaff = async () => {
    if (!formData.role || !formData.assignedBranch) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in all required fields",
        confirmButtonColor: "#FF6F3C"
      })
      return
    }

    const branch = branches.find(b => b.branch_id === formData.assignedBranch)

    if (!branch?.branch_owner?.trim() || !branch?.email?.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Branch Owner Info Missing",
        text: "This branch needs a Branch Owner name and email set up before an account can be created for it.",
        confirmButtonColor: "#FF6F3C"
      })
      return
    }

    try {
      const { first: ownerFirstName, last: ownerLastName } = splitOwnerName(branch.branch_owner)

      // Generate a short cosmetic company email, e.g. tv-brv-juan@kaperk.com
      // NOTE: this is just for branch/role identification — it's NOT used for login or OTP.
      const branchSlug = branch.name
        .replace(/[^a-zA-Z\s]/g, '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(w => w[0])
        .join('')
        .toLowerCase()
      const roleSlug = formData.role.toLowerCase().slice(0, 3) // "admin" -> "adm", "barista" -> "bar"
      let companyEmail = `${branchSlug}-${roleSlug}@kaperk.com`

      // Keep company emails unique — matches staff_company_email_key
      let suffix = 1
      while (true) {
        const { data: existing, error: existingError } = await supabase
          .from('staff')
          .select('id')
          .eq('company_email', companyEmail)
          .maybeSingle()

        if (existingError) throw existingError
        if (!existing) break

        suffix += 1
        companyEmail = `${branchSlug}-${roleSlug}-${suffix}@kaperk.com`
      }

      const { data: staffData, error: staffError } = await supabase
        .from('staff')
        .insert([
          {
            first_name: ownerFirstName, // sourced from BRANCHES.branch_owner
            last_name: ownerLastName, // sourced from BRANCHES.branch_owner
            email: branch.email, // Branch Owner email — used for login + OTP
            company_email: companyEmail, // generated — this is what they log in with
            phone_number: branch.phone_number,
            role: formData.role,
            branch_id: formData.assignedBranch,
            start_date: new Date().toISOString().split('T')[0], // auto-set to account creation date
            status: "Active",
            permissions: getPermissionsByRole(formData.role)
          }
        ])
        .select()
        .single()

      if (staffError) throw staffError

      const { data: accountData, error: accountError } = await supabase
        .rpc('create_staff_account', {
          p_staff_id: staffData.id,
          p_email: companyEmail, // generated email — this is the actual login credential
          p_default_password: 'Staff@123'
        })

      if (accountError) {
        await supabase.from('staff').delete().eq('id', staffData.id)
        throw accountError
      }

      // Fire off the welcome email via Edge Function — sent to their real email
      try {
        const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
          body: {
            firstName: ownerFirstName,
            loginEmail: companyEmail,     // shown in the email body — this is what they sign in with
            deliveryEmail: branch.email, // Branch Owner email — this is where the email/OTP actually gets sent
            tempPassword: 'Staff@123',
            role: formData.role,
            branchName: branch ? branch.name : ''
          }
        })
        if (emailError) console.error("Welcome email failed:", emailError)
      } catch (emailErr) {
        console.error("Welcome email failed to send:", emailErr)
        // Non-fatal — staff account was still created successfully
      }

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        companyEmail: "",
        phoneNumber: "",
        role: "",
        assignedBranch: "",
        employmentType: "",
        permissions: {
          canViewAnalytics: false,
          canManageOrders: false,
          canAccessCustomerData: false,
          canModifyInventory: false
        }
      })
      setShowAddModal(false)

      Swal.fire({
        icon: "success",
        title: "Staff Added!",
        html: `
                <p>An account for <strong>${branch.name}</strong> has been successfully added to the team</p>
                <p class="mt-2 text-sm text-gray-600">Default password: <strong>Staff@123</strong></p>
                <p class="text-xs text-gray-500">Staff member will be required to change password on first login</p>
            `,
        confirmButtonColor: "#FF6F3C",
        timer: 5000
      })

      fetchstaff()
    } catch (err) {
      console.error("Error adding staff:", err)
      Swal.fire({
        icon: "error",
        title: "Failed to Add Staff",
        text: getFriendlyError(err),
        confirmButtonColor: "#FF6F3C"
      })
    }
  }

  const handleEditClick = (member) => {
    setEditingStaff(member)
    const original = {
      firstName: member.first_name,
      lastName: member.last_name,
      email: member.email,
      phoneNumber: member.phone_number,
      role: member.role,
      assignedBranch: member.branch_id || "",
      startDate: member.start_date,
      employmentType: member.employment_type,
      permissions: member.permissions || {
        canViewAnalytics: false,
        canManageOrders: false,
        canAccessCustomerData: false,
        canModifyInventory: false
      }
    }
    setFormData(original)
    setOriginalFormData(original)
    setShowEditModal(true)
  }

  const handleUpdateStaff = async () => {
    if (!formData.role || !formData.assignedBranch) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in all required fields",
        confirmButtonColor: "#FF6F3C"
      })
      return
    }

    const branch = branches.find(b => b.branch_id === formData.assignedBranch)

    if (!branch?.branch_owner?.trim() || !branch?.email?.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Branch Owner Info Missing",
        text: "This branch needs a Branch Owner name and email set up before its account can be updated.",
        confirmButtonColor: "#FF6F3C"
      })
      return
    }

    try {
      const { first: ownerFirstName, last: ownerLastName } = splitOwnerName(branch.branch_owner)

      const { data, error } = await supabase
        .from('staff')
        .update({
          first_name: ownerFirstName,
          last_name: ownerLastName,
          email: branch.email,
          phone_number: branch.phone_number,
          role: formData.role,
          branch_id: formData.assignedBranch,
          start_date: formData.startDate,
          permissions: getPermissionsByRole(formData.role)
        })
        .eq('id', editingStaff.id)
        .select()

      if (error) throw error

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        role: "",
        assignedBranch: "",
        startDate: "",
        employmentType: "",
        permissions: {
          canViewAnalytics: false,
          canManageOrders: false,
          canAccessCustomerData: false,
          canModifyInventory: false
        }
      })
      setShowEditModal(false)
      setEditingStaff(null)

      Swal.fire({
        icon: "success",
        title: "Staff Updated!",
        text: `${branch.name}'s account information has been successfully updated`,
        confirmButtonColor: "#FF6F3C",
        timer: 3000
      })

      fetchstaff()
    } catch (err) {
      console.error("Error updating staff:", err)
      Swal.fire({
        icon: "error",
        title: "Failed to Update Staff",
        text: getFriendlyError(err),
        confirmButtonColor: "#FF6F3C"
      })
    }
  }

  const handleArchiveClick = (member) => {
    setArchivingStaff(member)
    setArchiveReason("")
    setShowArchiveModal(true)
  }

  const handleArchiveStaff = async () => {
    if (!archiveReason.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Reason Required",
        text: "Please provide a reason for archiving this staff member",
        confirmButtonColor: "#FF6F3C"
      })
      return
    }

    try {
      const { data, error } = await supabase.rpc('archive_staff_member', {
        p_staff_id: archivingStaff.id,
        p_archive_reason: archiveReason.trim(),
        p_archived_by: null
      })

      if (error) throw error

      setShowArchiveModal(false)
      setArchivingStaff(null)
      setArchiveReason("")

      Swal.fire({
        icon: "success",
        title: "Staff Archived",
        html: `
                    <p>${archivingStaff.branches?.name || "This branch's"} account has been archived</p>
                    <p class="text-sm text-gray-600 mt-2">The staff member's data has been moved to the archive for record-keeping</p>
                `,
        confirmButtonColor: "#FF6F3C",
        timer: 3000
      })

      fetchstaff()
    } catch (err) {
      console.error("Error archiving staff:", err)
      Swal.fire({
        icon: "error",
        title: "Failed to Archive",
        text: getFriendlyError(err),
        confirmButtonColor: "#FF6F3C"
      })
    }
  }

  const handleViewClick = (member) => {
    setViewingStaff(member)
    setShowViewModal(true)
  }

  const filteredStaff = staff.filter(member => {
    const matchesSearch =
      member.branches?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.company_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.role?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesRole = filterRole === "All" || member.role === filterRole

    return matchesSearch && matchesRole
  })

  const totalPages = Math.max(1, Math.ceil(filteredStaff.length / itemsPerPage))
  const paginatedStaff = filteredStaff.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const hasChanges = originalFormData ? (
    formData.firstName !== originalFormData.firstName ||
    formData.lastName !== originalFormData.lastName ||
    formData.email !== originalFormData.email ||
    formData.phoneNumber !== originalFormData.phoneNumber ||
    formData.role !== originalFormData.role ||
    formData.assignedBranch !== originalFormData.assignedBranch ||
    formData.startDate !== originalFormData.startDate
  ) : false


  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <Header
        title="Branch Account Management"
        subtitle="Manage your team members and their permissions across all branches"
        onGoToProfile={onGoToProfile}
      />

      <div className="p-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 15 15"><path fill="currentColor" d="M5 8.9c1.44 0 2.68.252 3.575.855C9.502 10.38 10 11.343 10 12.6a.501.501 0 0 1-1 0c0-.958-.358-1.596-.983-2.017C7.359 10.141 6.35 9.9 5 9.9s-2.36.241-3.017.684C1.358 11.005 1 11.643 1 12.601a.501.501 0 0 1-1 0c0-1.258.497-2.221 1.424-2.846C2.319 9.152 3.56 8.9 5 8.9m4.975 0c1.439 0 2.68.252 3.575.855c.927.625 1.425 1.588 1.425 2.846a.5.5 0 0 1-1 0c0-.958-.358-1.596-.984-2.017c-.518-.349-1.253-.57-2.202-.65a4.5 4.5 0 0 0-.87-1.033zM5 1.85a3.151 3.151 0 0 1 0 6.3a3.15 3.15 0 1 1 0-6.3m4.975 0a3.15 3.15 0 0 1 0 6.3c-.524 0-1.016-.13-1.45-.356a4.5 4.5 0 0 0 .534-.852a2.15 2.15 0 1 0 0-3.887a4.5 4.5 0 0 0-.535-.85a3.1 3.1 0 0 1 1.45-.355M5 2.85a2.151 2.151 0 0 0 0 4.3a2.15 2.15 0 0 0 0-4.3" /></svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Staff</p>
                <p className="text-3xl font-bold text-gray-800">{stats.totalStaff}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48"><g fill="none"><path stroke="currentColor" strokeLinejoin="round" strokeWidth="4" d="M19 20a7 7 0 1 0 0-14a7 7 0 0 0 0 14Z" /><path d="M36 29v12zm-6 6h12z" clipRule="evenodd" /><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M36 29v12m-6-6h12m-15-7h-8.2c-4.48 0-6.72 0-8.432.872a8 8 0 0 0-3.496 3.496C6 34.08 6 36.32 6 40.8V42h21" /></g></svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Baristas</p>
                <p className="text-3xl font-bold text-gray-800">{stats.activeBaristas}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" d="M16 12c2.374 1.183 4 3.65 4 7v4H4v-4c0-3.354 1.631-5.825 4-7m4 1a6 6 0 1 0 0-12a6 6 0 0 0 0 12Zm6-6c-1.5 0-3 .36-5-2c-2 2.36-4.5 3-7 2m1 6l5.025 5.257L17 13m-5 5v5" /></svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Admins</p>
                <p className="text-3xl font-bold text-gray-800">{stats.admins}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"><path d="M12 10h.01M12 14h.01M12 6h.01M16 10h.01M16 14h.01M16 6h.01M8 10h.01M8 14h.01M8 6h.01M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" /><rect width="16" height="20" x="4" y="2" rx="2" /></g></svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Branches</p>
                <p className="text-3xl font-bold text-gray-800">{stats.branches}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Staff Table */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 flex-1">
                <div className="relative flex-1 max-w-md w-full">
                  <input
                    type="text"
                    placeholder="Search branch accounts by name, email, or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6F3C]"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" /></svg>
                  </span>
                </div>

                <button className="px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" fillRule="evenodd"><path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z" /><path fill="currentColor" d="M3 4.5A1.5 1.5 0 0 1 4.5 3h15A1.5 1.5 0 0 1 21 4.5v2.086A2 2 0 0 1 20.414 8L15 13.414v7.424a1.1 1.1 0 0 1-1.592.984l-3.717-1.858A1.25 1.25 0 0 1 9 18.846v-5.432L3.586 8A2 2 0 0 1 3 6.586zM5 5v1.586l5.56 5.56a1.5 1.5 0 0 1 .44 1.061v5.175l2 1v-6.175c0-.398.158-.78.44-1.06L19 6.585V5z" /></g></svg>
                  <span>Filter</span>
                </button>
              </div>

              <div className='flex items-center gap-3'>
                <button
                  onClick={() => setShowArchiveView(true)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                  </svg>
                  View Archive
                </button>

                <button
                  onClick={() => {
                    setFormData({
                      firstName: "",
                      lastName: "",
                      email: "",
                      companyEmail: "",
                      phoneNumber: "",
                      role: "",
                      assignedBranch: "",
                      employmentType: "",
                      permissions: {
                        canViewAnalytics: false,
                        canManageOrders: false,
                        canAccessCustomerData: false,
                        canModifyInventory: false
                      }
                    })
                    setShowAddModal(true)
                  }}
                  className="px-4 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-yellow-600 flex items-center gap-2"
                >
                  <span>+</span>
                  Add Branch Account
                </button>
              </div>
            </div>

            {/* Role Filter Tabs */}
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => setFilterRole('All')}
                className={`px-4 py-2 rounded-lg font-medium ${filterRole === 'All'
                  ? 'bg-[#FF6F3C] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                All
              </button>

              <button
                onClick={() => setFilterRole('Barista')}
                className={`px-4 py-2 rounded-lg font-medium ${filterRole === 'Barista'
                  ? 'bg-[#FF6F3C] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                Baristas
              </button>

              <button
                onClick={() => setFilterRole('Admin')}
                className={`px-4 py-2 rounded-lg font-medium ${filterRole === 'Admin'
                  ? 'bg-[#FF6F3C] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                Admins
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Join Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      Loading staff...
                    </td>
                  </tr>
                ) : filteredStaff.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No staff members found
                    </td>
                  </tr>
                ) : (
                  paginatedStaff.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="font-medium text-gray-800">
                            {member.branches?.name || "Unassigned"}
                          </p>
                          <p className="text-sm text-gray-500">{member.company_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${member.role === 'Barista'
                          ? 'bg-blue-100 text-blue-800'
                          : member.role === 'Admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                          }`}>
                          {member.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                        {member.branches?.name || "Unassigned"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${member.status === 'Active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                        {new Date(member.start_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            onClick={() => handleEditClick(member)}
                            title="Edit Staff">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" /></svg>
                          </button>
                          <button
                            onClick={() => handleArchiveClick(member)}
                            className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                            title="Archive Staff"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" /></svg>
                          </button>
                          <button className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                            onClick={() => handleViewClick(member)}
                            title="View Details">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 5.92A.96.96 0 1 0 12 4a.96.96 0 0 0 0 1.92m0 7.04a.96.96 0 1 0 0-1.92a.96.96 0 0 0 0 1.92M12 20a.96.96 0 1 0 0-1.92a.96.96 0 0 0 0 1.92" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredStaff.length > 0 && (
            <Pagination
              page={currentPage}
              totalItems={filteredStaff.length}
              pageSize={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      {/* Archive Confirmation Modal */}
      {showArchiveModal && archivingStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setShowArchiveModal(false)
              setArchivingStaff(null)
              setArchiveReason("")
            }}
          ></div>

          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-bold text-gray-800">Archive Branch Account</h2>
              <p className="text-sm text-gray-600 mt-1">This will move the branch account to the archive</p>
            </div>

            <div className="px-6 py-6 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="text-orange-600 flex-shrink-0">
                    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m1 15h-2v-2h2zm0-4h-2V7h2z" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-800">
                      You are about to archive the account for {archivingStaff.branches?.name || "this branch"}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Their account will be disabled and data moved to archive for record-keeping.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Archiving <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="e.g., Resigned, Contract ended, Performance issues, etc."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] resize-none"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowArchiveModal(false)
                  setArchivingStaff(null)
                  setArchiveReason("")
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveStaff}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700"
              >
                Archive Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Branch Account Modal*/}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowAddModal(false)}
          ></div>

          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-bold text-gray-800">Add New Branch Account</h2>
              <p className="text-sm text-gray-600 mt-1">Create an Admin or Barista account for a branch</p>
            </div>

            <div className="px-6 py-6 space-y-6">

              {/* Personal Information — read-only, sourced from the branch's Branch Owner */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-600">
                  <p>
                    Name, email, and phone number for this account are pulled from the assigned
                    branch's <strong>Branch Owner</strong> details. To update them, edit the branch
                    under Branch Management.
                  </p>
                </div>
              </div>

              {/* Account Type and Branch */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Type & Branch</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                  >
                    <option value="">Select account type...</option>
                    <option value="Barista">Barista</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned Branch <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.assignedBranch}
                      onChange={(e) => setFormData({ ...formData, assignedBranch: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                    >
                      <option value="">Select branch...</option>
                      {branches.map((branch) => (
                        <option key={branch.branch_id} value={branch.branch_id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {formData.role && formData.assignedBranch && (
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-500 mb-1">Generated Login Email</p>
                    <p className="text-sm font-mono font-medium text-gray-800">{formData.companyEmail}</p>
                  </div>
                )}
              </div>

            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddStaff}
                className="px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b]"
              >
                Create Branch Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setShowEditModal(false)
              setEditingStaff(null)
            }}
          ></div>

          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-bold text-gray-800">Edit Staff Member</h2>
              <p className="text-sm text-gray-600 mt-1">Update the staff member's information</p>
            </div>

            <div className="px-6 py-6 space-y-6">
              <StaffFormFields formData={formData} setFormData={setFormData} branches={branches} mode="edit" />
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingStaff(null)
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStaff}
                disabled={!hasChanges}
                className={`px-6 py-2 text-white rounded-lg font-medium transition-colors ${hasChanges
                  ? "bg-[#FF6F3C] hover:bg-[#ff4c0b] cursor-pointer"
                  : "bg-gray-300 cursor-not-allowed"
                  }`}
              >
                {hasChanges ? "Update Staff" : "No Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setShowViewModal(false)
              setViewingStaff(null)
            }}
          ></div>

          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-bold text-gray-800">Staff Details</h2>
              <p className="text-sm text-gray-600 mt-1">View complete information for this staff member</p>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                  <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Branch</label>
                  <p className="text-gray-800 font-medium">{viewingStaff.branches?.name || "Unassigned"}</p>
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Login Email</label>
                    <p className="text-gray-800 font-medium">{viewingStaff.company_email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Branch Owner Email</label>
                    <p className="text-gray-800 font-medium">{viewingStaff.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Phone Number</label>
                    <p className="text-gray-800 font-medium">{viewingStaff.phone_number}</p>
                  </div>
                </div>
              </div>

              {/* Roles and Access */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Roles and Access</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                  <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Role</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${viewingStaff.role === 'Barista'
                    ? 'bg-blue-100 text-blue-800'
                    : viewingStaff.role === 'Admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-800'
                    }`}>
                    {viewingStaff.role}
                  </span>
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Assigned Branch</label>
                    <p className="text-gray-800 font-medium">
                      {viewingStaff.branches?.name || "Unassigned"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                  <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Start Date</label>
                  <p className="text-gray-800 font-medium">
                    {new Date(viewingStaff.start_date).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${viewingStaff.status === 'Active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}>
                      {viewingStaff.status}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Staff ID</label>
                    <p className="text-gray-800 font-medium font-mono text-sm">{viewingStaff.id}</p>
                  </div>
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingStaff(null)
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingStaff(null)
                  handleEditClick(viewingStaff)
                }}
                className="px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b]"
              >
                Edit Staff
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}